// react
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useNavigate } from 'react-router-dom';

// components
import { Button, Container, ProgressBar } from '@/components/atoms';
import { Icons } from '@/components/atoms/Icon/icons';
import { BackButton } from '@/components/molecules';

// hooks
import { getColorContrast } from '@/utils/color';

// theme
import { Color } from '@/theme/color';

export enum OnboardingActionType {
  Next = 'next',
  Skip = 'nextSection',
  Back = 'back',
  End = 'end',
  Previous = 'previous',
  To = 'to',
}

const defaultActions = [
  {
    label: 'Back',
    onClick: OnboardingActionType.Back,
  },
  {
    label: 'Next',
    onClick: OnboardingActionType.Next,
  },
];

/**
 * OnboardingFlowPage takes the user through the onboarding flow.
 */
export const OnboardingFlow = <ConfigItem,>({
  className,
  config,
  onNext,
  onComplete,
  isActionBackground = true,
  alignActions = 'center',
  isBackButton = false,
}: OnboardingFlowProps<ConfigItem & OnboardingActions>) => {
  // state
  const [index, setIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(0);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  // refs
  const wrapperRef = useRef<HTMLDivElement>(null);

  // hooks
  const navigate = useNavigate();

  // memos
  const CurrentComponent = useMemo(() => {
    return config[index].Component;
  }, [index, config]);

  /**
   * actions are the buttons that are displayed at the bottom of the onboarding
   * flow. If the actions are not provided, the default actions are used.
   *
   * Special case: if the actions are set to null, no actions are displayed.
   */
  const actions = useMemo(() => {
    const subConfig = config[index].config[subIndex];
    if (!subConfig) return null;

    const configActions = config[index].config[subIndex].actions;
    if (configActions === null) return null;

    return configActions ?? defaultActions;
  }, [config, index, subIndex]);

  const progressLimit = useMemo(
    () =>
      config.reduce((limit, item) => {
        return (
          limit +
          item.config.filter((subItem) => !subItem.isIgnoreProgress).length
        );
      }, 0),
    [config],
  );

  const progress = useMemo(() => {
    const progress = config.slice(0, index).reduce((total, item) => {
      return (
        total +
        item.config.filter((subItem) => !subItem.isIgnoreProgress).length
      );
    }, 0);
    return progress + subIndex;
  }, [index, subIndex, config]);

  // methods
  const handleComplete = useCallback(() => {
    setIsComplete(true);
    onComplete();
  }, [onComplete]);

  const handleClick = useCallback(
    async (actionType: OnboardingActionType) => {
      switch (actionType) {
        case OnboardingActionType.Next:
          if (subIndex === config[index].config.length - 1) {
            if (
              config[index].onBeforeNext &&
              !(await config[index].onBeforeNext?.())
            ) {
              return;
            }
            if (index >= config.length - 1) {
              handleComplete();
              return;
            }
            setIndex(Math.min(index + 1, config.length - 1));
            setSubIndex(0);
          } else {
            setSubIndex((prev) => {
              return Math.min(prev + 1, config[index].config.length - 1);
            });
          }
          break;
        case OnboardingActionType.Back:
          if (subIndex === 0) {
            if (index <= 0) return;
            const prevIndex = index - 1;
            setIndex(prevIndex);
            setSubIndex(config[prevIndex].config.length - 1);
          } else {
            setSubIndex((prev) => prev - 1);
          }
          break;
        case OnboardingActionType.Skip:
          if (index >= config.length - 1) return;
          setIndex(Math.min(index + 1, config.length - 1));
          setSubIndex(0);
          break;
        case OnboardingActionType.End:
          handleComplete();
          return;
      }
    },
    [index, subIndex, config, onComplete],
  );

  // effects
  useEffect(() => {
    if (isComplete) return;
    config[index].onStart?.(handleClick);
  }, [index, config, handleClick]);

  useEffect(() => {
    config[index].isSkip && handleClick(OnboardingActionType.Skip);
  }, [config, index]);

  useEffect(() => {
    wrapperRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    onNext?.(config[index]?.id, config[index].config[subIndex]?.id);
  }, [index, subIndex]);

  return (
    <>
      <div style={{ display: 'none' }}>
        {config?.[index]?.isProgressBar ? 'progress bar' : 'no progress bar'}
      </div>
      {config?.[index]?.isProgressBar && (
        <StyledContainer isInline size='md'>
          <ProgressBarWrapper>
            {isBackButton && <BackButton label={undefined} variant='link' />}
            <ProgressBar
              limit={progressLimit}
              progress={progress}
              label='Progress'
            />
          </ProgressBarWrapper>
        </StyledContainer>
      )}
      <Wrapper className={className} ref={wrapperRef}>
        <CurrentComponent
          config={config[index].config}
          handleClick={handleClick}
          currentIndex={subIndex || 0}
          isNextDisabled={isNextDisabled}
          setIsNextDisabled={setIsNextDisabled}
          isComplete={isComplete}
        />
      </Wrapper>
      {config[index]?.config[subIndex]?.isSupport && (
        <Support>
          For any urgent issues, please email us at{' '}
          <Button
            variant='link'
            ariaLabel='email support'
            to='mailto:support@karehero.com'
            isInline
          >
            support@karehero.com
          </Button>{' '}
          or book a call{' '}
          <Button
            variant='link'
            ariaLabel='book a call'
            to='/book-call?category=karehero-call'
            isInline
          >
            here
          </Button>
        </Support>
      )}
      {actions && (
        <ButtonWrapper
          $isActionBackground={isActionBackground}
          $alignActions={alignActions}
          $color={config[index].config[subIndex]?.actionsBackground}
        >
          {actions?.map((action: OnboardingAction) => (
            <StyledButton
              key={action.label}
              ariaLabel='next'
              variant={
                action.onClick === OnboardingActionType.Next
                  ? 'primary'
                  : 'secondary'
              }
              onPress={() => {
                if (action.onClick === OnboardingActionType.To) {
                  action.to && navigate(action.to);
                  return;
                }
                handleClick(action.onClick);
              }}
              isDisabled={
                (action.onClick == OnboardingActionType.Next &&
                  isNextDisabled) ||
                action.isDisabled
              }
              iconLeft={action.iconLeft}
              iconRight={action.iconRight}
              $isFullWidth={action.isFullWidth}
              isDarkMode
            >
              {action.label}
            </StyledButton>
          ))}
        </ButtonWrapper>
      )}
    </>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex: 1;
`;

const StyledContainer = styled(Container)`
  padding-top: ${(props) => props.theme.spacing[32]};
  flex: 0;
`;

const ProgressBarWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[16]};
  padding-bottom: ${(props) => props.theme.spacing[24]};
`;

interface ButtonWrapperProps {
  $isActionBackground: boolean;
  $alignActions: AlignActions;
  $color?: Color;
}
const ButtonWrapper = styled.div<ButtonWrapperProps>`
  display: flex;
  ${(props) => {
    switch (props.$alignActions) {
      case 'left':
        return css`
          justify-content: flex-start;
        `;
      case 'center':
        return css`
          justify-content: center;
        `;
      case 'right':
        return css`
          justify-content: flex-end;
        `;
      default:
        return css`
          justify-content: center;
        `;
    }
  }}
  gap: ${(props) => props.theme.spacing[16]};

  padding: ${(props) => props.theme.spacing[24]};
  padding-bottom: ${(props) => props.theme.spacing[36]};

  padding: ${(props) => props.theme.spacing[16]};
  border-top: 1px solid
    ${(props) => {
      if (!props.$color) return props.theme.color.neutral[50];
      if (getColorContrast(props.$color) === '#000000') {
        return props.theme.color.neutral[70];
      } else {
        return props.theme.color.neutral[50];
      }
    }};
  background: ${(props) => props.$color || props.theme.color.neutral[10]};
  ${(props) =>
    !props.$isActionBackground &&
    !props.$color &&
    css`
      background: none;
    `}
  z - index: 1;
`;

interface ButtonProps {
  $isFullWidth?: boolean;
}
const StyledButton = styled(Button)<ButtonProps>`
  min-width: 120px;
  max-width: 600px;
  ${(props) =>
    props.$isFullWidth &&
    css`
      width: 100%;
    `}
`;

const Support = styled.div`
  text-align: center;
  padding: ${(props) => props.theme.spacing[24]}
    ${(props) => props.theme.spacing[16]};
`;

/**
 * toGenericOnboarding converts a component which uses GenericFlowProps into
 * a type that can be consumed by OnboardingFlow.
 */
export const toGenericOnboarding = (
  component: React.FC<GenericFlowProps<any>>,
) => {
  return component as React.FC<GenericFlowProps<any>>;
};

interface OnboardingAction {
  label: string;
  onClick: OnboardingActionType;
  to?: string;
  iconLeft?: Icons;
  iconRight?: Icons;
  isDisabled?: boolean;
  isFullWidth?: boolean;
}

export interface OnboardingActions {
  id: string;
  isIgnoreProgress?: boolean;
  actions?: OnboardingAction[];
  actionsBackground?: Color;
  isSupport?: boolean;
}

export interface GenericFlowProps<ConfigItem extends OnboardingActions> {
  config: ConfigItem[];
  currentIndex: number;
  handleClick?: (action: OnboardingActionType) => void;
  isNextDisabled: boolean;
  setIsNextDisabled: (isNextDisabled: boolean) => void;
  isComplete: boolean;
}

export interface OnboardingFlowConfig<ConfigItem> {
  id: string;
  Component: React.FC<GenericFlowProps<ConfigItem & OnboardingActions>>;
  config: ConfigItem[];
  actions?: OnboardingAction[];
  onBeforeNext?: () => Promise<boolean>;
  onStart?: (handleClick: (action: OnboardingActionType) => void) => void;
  isProgressBar?: boolean;
  isSkip?: boolean;
}

type AlignActions = 'left' | 'center' | 'right';
interface OnboardingFlowProps<ConfigItem> {
  className?: string;
  config: OnboardingFlowConfig<ConfigItem>[];
  onNext?: (configID: string, subConfigID: string) => void;
  onComplete: () => void;
  isActionBackground?: boolean;
  alignActions?: AlignActions;
  isBackButton?: boolean;
}

OnboardingFlow.displayName = 'OnboardingFlow';
export default OnboardingFlow;
